import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exportScheduleTemplateToCsv,
  fetchScheduleTemplateById,
  normalizeScheduleTemplateItem,
  saveScheduleTemplateItems,
  saveScheduleTemplateLinks,
  updateScheduleTemplateMeta,
  type TemplateItemDraft,
  type TemplateLinkDraft,
} from '@/lib/scheduleTemplateManagement';
import type { ScheduleTemplateItem, ScheduleTemplateLink } from '@/lib/scheduleUtils';
import ScheduleTemplateEditorLinksSection, {
  type EditableTemplateLink,
} from './ScheduleTemplateEditorLinksSection';

interface EditableItem extends ScheduleTemplateItem {
  _localId: string;
}

interface Props {
  open: boolean;
  templateId: string | null;
  onClose: () => void;
  onSaved?: (result: { isDraft: boolean; itemCount: number }) => void;
}

function toEditableItem(item: ScheduleTemplateItem, index: number): EditableItem {
  const normalized = normalizeScheduleTemplateItem(
    { ...item } as Record<string, unknown>,
    item.template_id
  );
  return { ...normalized, _localId: normalized.id || `local-${index}` };
}

function collectEditableItems(
  templateId: string,
  items: ScheduleTemplateItem[] | undefined,
  sections: { items?: ScheduleTemplateItem[] }[] | undefined
): ScheduleTemplateItem[] {
  const flat = Array.isArray(items) ? items : [];
  if (flat.length > 0) {
    return flat.map((row) => normalizeScheduleTemplateItem(row as Record<string, unknown>, templateId));
  }
  const sectionList = Array.isArray(sections) ? sections : [];
  return sectionList.flatMap((section) =>
    Array.isArray(section?.items)
      ? section.items.map((row) =>
          normalizeScheduleTemplateItem(row as Record<string, unknown>, templateId)
        )
      : []
  );
}

function createBlankItem(templateId: string, sortOrder: number): EditableItem {
  return {
    id: '',
    template_id: templateId,
    sort_order: sortOrder,
    default_task_no: sortOrder,
    title: '',
    phase: null,
    default_duration_days: 1,
    default_is_included: true,
    default_assigned_role: null,
    default_assigned_to: null,
    notes: null,
    _localId: `new-${Date.now()}-${sortOrder}`,
  };
}

function toEditableLink(link: ScheduleTemplateLink, index: number): EditableTemplateLink {
  return {
    ...link,
    _localId: link.id || `local-link-${index}`,
  };
}

export default function ScheduleTemplateEditorModal({ open, templateId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [projectType, setProjectType] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [links, setLinks] = useState<EditableTemplateLink[]>([]);
  const [originalItemIds, setOriginalItemIds] = useState<string[]>([]);
  const [originalLinkIds, setOriginalLinkIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditableItem | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadTemplate = useCallback(async (id: string) => {
    setLoading(true);
    setLoadError(null);
    setEditingId(null);
    setEditDraft(null);
    setSaveError(null);
    setSaveSuccess(false);
    setSearch('');

    try {
      const result = await fetchScheduleTemplateById(id);
      if (!result.template) {
        setLoadError(result.loadError ?? 'Template not found');
        setItems([]);
        setLinks([]);
        setOriginalItemIds([]);
        setOriginalLinkIds([]);
        return;
      }

      const editableRows = collectEditableItems(id, result.items, result.sections);

      setTemplateName(result.template.name ?? '');
      setProjectType(result.template.project_type ?? '');
      setIsDefault(result.template.is_default === true);
      setItems(editableRows.map(toEditableItem));
      setLinks((result.links ?? []).map(toEditableLink));
      setOriginalItemIds(editableRows.map((i) => i.id).filter(Boolean));
      setOriginalLinkIds((result.links ?? []).map((l) => l.id).filter(Boolean));
      setLoadError(result.loadError);
    } catch (err) {
      console.warn('[ScheduleTemplateEditorModal] load failed:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load template');
      setItems([]);
      setLinks([]);
      setOriginalItemIds([]);
      setOriginalLinkIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && templateId) {
      void loadTemplate(templateId);
    }
  }, [open, templateId, loadTemplate]);

  const filteredItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        (item.title ?? '').toLowerCase().includes(q) ||
        (item.phase ?? '').toLowerCase().includes(q) ||
        String(item.sort_order ?? '').includes(q)
    );
  }, [items, search]);

  if (!open || !templateId) return null;

  const handleAddItem = () => {
    const orders = items.map((i) => (Number.isFinite(i.sort_order) ? i.sort_order : 0));
    const nextOrder = orders.length > 0 ? Math.max(...orders) + 1 : 1;
    const newItem = createBlankItem(templateId, nextOrder);
    setItems((prev) => [...prev, newItem]);
    setEditingId(newItem._localId);
    setEditDraft({ ...newItem });
  };

  const handleStartEdit = (item: EditableItem) => {
    setEditingId(item._localId);
    setEditDraft({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const handleSaveEdit = () => {
    if (!editDraft) return;
    setItems((prev) =>
      prev.map((item) => (item._localId === editDraft._localId ? { ...editDraft } : item))
    );
    setEditingId(null);
    setEditDraft(null);
  };

  const handleDeleteItem = (localId: string) => {
    setItems((prev) => prev.filter((item) => item._localId !== localId));
    if (editingId === localId) {
      setEditingId(null);
      setEditDraft(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const metaResult = await updateScheduleTemplateMeta(templateId, { name: templateName });
    if (metaResult.error) {
      setSaveError(metaResult.error);
      setSaving(false);
      return;
    }

    const currentIds = new Set(items.filter((i) => i.id).map((i) => i.id as string));
    const removedItemIds = originalItemIds.filter((id) => !currentIds.has(id));

    const drafts: TemplateItemDraft[] = items.map((item) => ({
      id: item.id || undefined,
      sort_order: item.sort_order,
      default_task_no: item.default_task_no,
      title: item.title,
      phase: item.phase,
      default_duration_days: item.default_duration_days,
      default_is_included: item.default_is_included,
      default_assigned_role: item.default_assigned_role,
      default_assigned_to: item.default_assigned_to,
      notes: item.notes,
    }));

    const itemsResult = await saveScheduleTemplateItems(templateId, drafts, removedItemIds);
    if (itemsResult.error) {
      setSaveError(itemsResult.error);
      setSaving(false);
      return;
    }

    // Reload items so new rows have DB ids before saving links
    const refreshed = await fetchScheduleTemplateById(templateId);
    const refreshedItems = collectEditableItems(templateId, refreshed.items, refreshed.sections);
    const itemIdSet = new Set(refreshedItems.map((i) => i.id).filter(Boolean));

    const currentLinkIds = new Set(links.filter((l) => l.id).map((l) => l.id as string));
    const removedLinkIds = originalLinkIds.filter((id) => !currentLinkIds.has(id));

    const linkDrafts: TemplateLinkDraft[] = links
      .filter(
        (link) =>
          itemIdSet.has(link.predecessor_item_id) && itemIdSet.has(link.successor_item_id)
      )
      .map((link) => ({
        id: link.id || undefined,
        predecessor_item_id: link.predecessor_item_id,
        successor_item_id: link.successor_item_id,
        link_type: link.link_type,
        lag_days: link.lag_days ?? 0,
      }));

    const linksResult = await saveScheduleTemplateLinks(templateId, linkDrafts, removedLinkIds);
    setSaving(false);

    if (linksResult.error) {
      setSaveError(linksResult.error);
      return;
    }

    setSaveSuccess(true);
    const savedItemCount = refreshedItems.length;
    onSaved?.({ isDraft: savedItemCount === 0, itemCount: savedItemCount });
    onClose();
  };

  const handleExport = async () => {
    const result = await exportScheduleTemplateToCsv(templateId);
    if (result.error) setSaveError(result.error);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-3xl rounded-3xl overflow-hidden mx-4 max-h-[92vh] flex flex-col shadow-xl">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0 gap-3">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full text-[#1A1A1A] font-bold text-base border-0 border-b border-transparent focus:border-[#E8621A] focus:outline-none bg-transparent"
              placeholder={t('templates.templateName')}
            />
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-[#6B7280]">{projectType}</span>
              {items.length === 0 ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280]">
                  {t('templates.draftBadge')}
                </span>
              ) : (
                !isDefault && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
                    {t('templates.activeBadge')}
                  </span>
                )
              )}
              {isDefault && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                  {t('templates.defaultBadge')}
                </span>
              )}
              <span className="text-xs text-[#9CA3AF]">
                {t('templates.itemCount', { count: items.length })}
                {links.length > 0 && ` · ${links.length} link${links.length !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => void handleExport()}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer"
              title={t('templates.exportCsv')}
            >
              <i className="ri-download-2-line text-[#374151]"></i>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer"
            >
              <i className="ri-close-line text-[#374151]"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#9CA3AF] text-sm">
              <i className="ri-loader-4-line animate-spin text-xl mr-2"></i>
              {t('templates.loading')}
            </div>
          ) : (
            <>
              {loadError && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">
                  {t('templates.loadError')}: {loadError}
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('templates.searchItems')}
                  className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A]"
                />
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  {t('templates.addStageTask')}
                </button>
              </div>

              {filteredItems.length === 0 ? (
                <div className="bg-[#F9FAFB] rounded-xl border border-gray-100 px-4 py-6 text-center text-sm text-[#9CA3AF]">
                  {search ? t('templates.noSearchResults') : t('templates.noItems')}
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
                  {filteredItems.map((item) => {
                    const isEditing = editingId === item._localId && editDraft;

                    if (isEditing && editDraft) {
                      return (
                        <div
                          key={item._localId}
                          className="bg-[#F9FAFB] rounded-xl border border-[#E8621A]/30 p-3 space-y-2 sticky top-0 z-10"
                        >
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="number"
                              value={editDraft.sort_order}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  sort_order: Number(e.target.value) || 1,
                                })
                              }
                              placeholder={t('templates.sortOrder')}
                              className="px-2.5 py-2 rounded-lg border border-gray-200 text-sm"
                            />
                            <input
                              type="number"
                              value={editDraft.default_task_no ?? ''}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  default_task_no: e.target.value ? Number(e.target.value) : null,
                                })
                              }
                              placeholder="#"
                              className="px-2.5 py-2 rounded-lg border border-gray-200 text-sm"
                            />
                            <input
                              type="number"
                              value={editDraft.default_duration_days}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  default_duration_days: Number(e.target.value) || 1,
                                })
                              }
                              className="px-2.5 py-2 rounded-lg border border-gray-200 text-sm"
                            />
                          </div>
                          <input
                            type="text"
                            value={editDraft.title}
                            onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                            placeholder={t('templates.taskTitle')}
                            className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm"
                          />
                          <input
                            type="text"
                            value={editDraft.phase ?? ''}
                            onChange={(e) =>
                              setEditDraft({ ...editDraft, phase: e.target.value || null })
                            }
                            placeholder={t('templates.phase')}
                            className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleSaveEdit}
                              className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer"
                            >
                              {t('templates.applyEdit')}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-semibold cursor-pointer"
                            >
                              {t('btn.cancel')}
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={item._localId}
                        className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-2.5 py-2 hover:border-gray-200"
                      >
                        <span className="w-10 text-center text-[10px] font-semibold text-[#9CA3AF] flex-shrink-0">
                          {item.sort_order}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">
                            {item.title || t('templates.untitledTask')}
                          </p>
                          <p className="text-[10px] text-[#9CA3AF] truncate">
                            {item.phase ? `${item.phase} · ` : ''}
                            {item.default_duration_days} {t('templates.days')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleStartEdit(item)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-[#6B7280]"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item._localId)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-red-500"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-gray-100 pt-4">
                <ScheduleTemplateEditorLinksSection
                  items={items.filter((i) => i.id)}
                  links={links}
                  onChange={setLinks}
                  disabled={saving}
                />
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          {saveSuccess && (
            <div className="bg-[#F0FDF4] border border-[#86EFAC]/40 rounded-xl px-4 py-2 text-sm text-[#059669]">
              {t('templates.saved')}
            </div>
          )}
          {saveError && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-2 text-sm text-red-700">
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer"
            >
              {t('btn.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={loading || saving}
              className="px-4 py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer hover:bg-[#D45515] disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t('templates.saving')}
                </>
              ) : (
                t('btn.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
